import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createRectangleDefaults, type CreateRectangleDefaultsOptions } from './rectangle-defaults.factory.js';

/**
 * Subscribes to `place/rectangle` UiActions and materialises a new rectangle:
 * builds the RectangleObject via `createRectangleDefaults`, pushes an
 * `AddObjectCommand` onto history, then re-emits `selection/set` for the
 * new id (TR6).
 *
 * Per PRD-F1 T10: rectangles have no inline-edit lifecycle, so this
 * controller deliberately does NOT dispatch `edit/enter-rectangle`.
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * FC4: never publishes onto `selection$`/`tool$`/`viewport$` directly;
 * downstream adapters translate `selection/set` into a `selection$` write.
 */
export class RectanglePlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/rectangle' }> =>
                        a.type === 'place/rectangle',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[RectanglePlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/rectangle' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        // Stefan, 2026-09-03 — "choose properties before drawing": a value
        // staged in the options circle while the tool was armed (see
        // `stagePrePlacementProperty()` in `o2d-svg-optionsbar.element.ts`),
        // if any. Absent → the factory's own hardcoded defaults apply, exactly
        // as before this feature.
        const staged = this.#bus.stagedToolProperties$.value.rectangle;
        // Bugreport (Anke, 2026-09-03): "fehlen für: schatten, transparenz" —
        // both already staged fine on the bus (their intents were never
        // excluded), the factory just never accepted them as overrides.
        // Also added strokeStyle/cornerRadius, same gap, same fix.
        const overrides: CreateRectangleDefaultsOptions | undefined = staged && {
            strokeColor: staged['strokeColor'] as string | undefined,
            strokeWidth: staged['strokeWidth'] as number | undefined,
            fill: staged['fill'] as string | undefined,
            cornerRadius: staged['cornerRadius'] as number | undefined,
            boxShadow: staged['boxShadow'] as 'on' | undefined,
            backgroundOpacity: staged['backgroundOpacity'] as number | undefined,
            strokeStyle: staged['strokeStyle'] as 'solid' | 'dashed' | 'dotted' | undefined,
        };
        const rect = createRectangleDefaults(
            scale,
            { x: action.rect.x, y: action.rect.y },
            { width: action.rect.width, height: action.rect.height },
            overrides,
        );

        const cmd = new AddObjectCommand(rect);
        this.#history.push(cmd, this.#state);

        const newId = objectId(rect.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
    }
}
